/**
 * @module CheckboxGroup
 *
 * @see https://0.vuetifyjs.com/components/forms/checkbox
 *
 * @remarks
 * Group component for managing multiple checkboxes with tri-state support.
 * Provides group context to child Checkbox.Root components. Supports batch
 * operations (selectAll, unselectAll, toggleAll) and mixed/indeterminate states.
 */

<script lang="ts">
  // Components
  import { Atom } from '#v0/components/Atom'

  // Composables
  import { createContext } from '#v0/composables/createContext'

  // Transformers
  import { toElement } from '#v0/composables/toElement'

  // Utilities
  import { isUndefined } from '#v0/utilities'

  // Types
  import type { AtomProps } from '#v0/components/Atom'
  import type { GroupContext, GroupTicket } from '#v0/composables/createGroup'
  import type { ID } from '#v0/types'
  import type { MaybeRefOrGetter } from 'vue'

  /** Ticket for checkbox items with element reference for focus management */
  export interface CheckboxTicket extends GroupTicket {
    /** Element reference for group focus management */
    el?: MaybeRefOrGetter<Element | null | undefined>
  }

  /** Imperative handle exposed by Checkbox.Group. */
  export interface CheckboxGroupExpose {
    /** Focus the selected checkbox, or the first if none is selected. */
    focus: (options?: FocusOptions) => void
  }

  export interface CheckboxGroupProps extends AtomProps {
    /** Namespace for dependency injection */
    namespace?: string
    /** Disables the entire checkbox group */
    disabled?: boolean
    /** Auto-select non-disabled items on registration */
    enroll?: boolean
    /**
     * Controls mandatory checkbox behavior:
     * - false (default): No mandatory enforcement
     * - true: Prevents deselecting the last selected item
     * - `force`: Automatically selects the first non-disabled item on registration
     */
    mandatory?: boolean | 'force'
    /** Accessible name for the group */
    label?: string
    /** ID of element that labels this group */
    ariaLabelledby?: string
    /** ID of element that describes this group */
    ariaDescribedby?: string
  }

  export interface CheckboxGroupSlotProps {
    /** Whether the checkbox group is disabled */
    isDisabled: boolean
    /** Whether no items are currently selected */
    isNoneSelected: boolean
    /** Whether all selectable (non-disabled) items are selected */
    isAllSelected: boolean
    /** Whether some but not all selectable items are selected */
    isMixed: boolean
    /** Select an item by ID */
    select: (id: ID | ID[]) => void
    /** Unselect an item by ID */
    unselect: (id: ID | ID[]) => void
    /** Toggle an item's selection state by ID */
    toggle: (id: ID | ID[]) => void
    /** Select all selectable (non-disabled) items */
    selectAll: () => void
    /** Unselect all items (respects mandatory option) */
    unselectAll: () => void
    /** Toggle between all selected and none selected */
    toggleAll: () => void
    /** Attributes to bind to the root element */
    attrs: {
      'role': 'group'
      'aria-label': string | undefined
      'aria-labelledby': string | undefined
      'aria-describedby': string | undefined
    }
  }

  export const [useCheckboxGroup, provideCheckboxGroup] = /* @__PURE__ */ createContext<GroupContext<CheckboxTicket>>()
</script>

<script lang="ts" setup generic="T = unknown">
  // Composables
  import { createGroup } from '#v0/composables/createGroup'
  import { useProxyModel } from '#v0/composables/useProxyModel'

  // Utilities
  import { toRef, toValue } from 'vue'

  defineOptions({ name: 'CheckboxGroup' })

  defineSlots<{
    default: (props: CheckboxGroupSlotProps) => any
  }>()

  defineEmits<{
    'update:model-value': [value: T | T[]]
  }>()

  const {
    as = 'div',
    renderless,
    namespace = 'v0:checkbox:group',
    ariaLabelledby,
    ariaDescribedby,
    disabled = false,
    enroll = false,
    mandatory = false,
    label,
  } = defineProps<CheckboxGroupProps>()

  const model = defineModel<T | T[]>()

  const group = createGroup<CheckboxTicket>({
    disabled: toRef(() => disabled),
    enroll: toRef(() => enroll),
    mandatory: toRef(() => mandatory),
    events: true,
  })

  useProxyModel(group, model, { multiple: true })

  provideCheckboxGroup(namespace, group)

  function focus (options?: FocusOptions) {
    const id = [...group.selectedIds][0]
    const ticket = isUndefined(id) ? group.seek('first') : group.get(id)
    ;(toElement(toValue(ticket?.el)) as HTMLElement | undefined)?.focus(options)
  }

  defineExpose<CheckboxGroupExpose>({ focus })

  const slotProps = toRef((): CheckboxGroupSlotProps => ({
    isDisabled: toValue(group.disabled),
    isNoneSelected: group.isNoneSelected.value,
    isAllSelected: group.isAllSelected.value,
    isMixed: group.isMixed.value,
    select: group.select,
    unselect: group.unselect,
    toggle: group.toggle,
    selectAll: group.selectAll,
    unselectAll: group.unselectAll,
    toggleAll: group.toggleAll,
    attrs: {
      'role': 'group',
      'aria-label': label || undefined,
      'aria-labelledby': ariaLabelledby || undefined,
      'aria-describedby': ariaDescribedby || undefined,
    },
  }))
</script>

<template>
  <Atom
    v-bind="slotProps.attrs"
    :as
    :renderless
  >
    <slot v-bind="slotProps" />
  </Atom>
</template>
